import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import './index.css';

type CalQueueFunction = {
  (...args: unknown[]): unknown;
  loaded?: boolean;
  ns?: Record<string, CalQueueFunction>;
  q?: unknown[][];
};

declare global {
  interface Window {
    Cal?: CalQueueFunction;
  }
}

const CAL_LINK = 'hogcloud/30min';
const CAL_SCRIPT_URL = 'https://app.cal.com/embed/embed.js';

const stableIdentityKey = (value: string) => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash).toString(36);
};

const Schedule = () => {
  const location = useLocation();
  const { isLoading, profile } = useAuth();
  const attendeeName = profile?.name?.trim() || '';
  const attendeeEmail = profile?.email?.trim() || '';
  const locationState = location.state as { calendarRefresh?: number } | null;
  const refreshToken = String(locationState?.calendarRefresh ?? location.key);
  const identityKey = stableIdentityKey(`${attendeeName}|${attendeeEmail}`);
  const embedKey = `${refreshToken}-${identityKey}`.replace(/[^a-zA-Z0-9_-]/g, '');
  const calNamespace = `30min-${embedKey}`;
  const inlineElementId = `my-cal-inline-${embedKey}`;
  const prefillParams = new URLSearchParams();
  if (attendeeName) prefillParams.set('name', attendeeName);
  if (attendeeEmail) prefillParams.set('email', attendeeEmail);
  const prefilledCalLink = prefillParams.size
    ? `${CAL_LINK}?${prefillParams.toString()}`
    : CAL_LINK;

  useEffect(() => {
    if (isLoading) return undefined;

    const initializeCalEmbed = () => {
      const targetElement = document.getElementById(inlineElementId);
      if (targetElement) {
        targetElement.innerHTML = '';
      }

      ((C, A, L) => {
        const pushQueue = (api: CalQueueFunction, args: IArguments | unknown[]) => {
          api.q = api.q || [];
          api.q.push(Array.from(args));
        };
        const documentRef = C.document;

        C.Cal =
          C.Cal ||
          function calEmbedApi() {
            const cal = C.Cal as CalQueueFunction;
            const args = arguments;

            if (!cal.loaded) {
              cal.ns = {};
              cal.q = cal.q || [];
              documentRef.head.appendChild(documentRef.createElement('script')).src = A;
              cal.loaded = true;
            }

            if (args[0] === L) {
              const api: CalQueueFunction = function namespaceApi() {
                pushQueue(api, arguments);
              };
              const namespace = args[1];
              api.q = api.q || [];

              if (typeof namespace === 'string') {
                cal.ns = cal.ns || {};
                cal.ns[namespace] = cal.ns[namespace] || api;
                pushQueue(cal.ns[namespace], args);
                pushQueue(cal, ['initNamespace', namespace]);
              } else {
                pushQueue(cal, args);
              }

              return;
            }

            pushQueue(cal, args);
          };
      })(window, CAL_SCRIPT_URL, 'init');

      window.Cal?.('init', calNamespace, { origin: 'https://app.cal.com' });
      window.Cal?.ns?.[calNamespace]?.('inline', {
        elementOrSelector: `#${inlineElementId}`,
        config: {
          layout: 'month_view',
          useSlotsViewOnSmallScreen: 'true',
          ...(attendeeName ? { name: attendeeName } : {}),
          ...(attendeeEmail ? { email: attendeeEmail } : {}),
        },
        calLink: prefilledCalLink,
      });
      window.Cal?.ns?.[calNamespace]?.('ui', {
        cssVarsPerTheme: {
          light: {
            'cal-brand': '#91011E',
          },
          dark: {
            'cal-brand': '#fafafa',
          },
        },
        hideEventTypeDetails: false,
        layout: 'month_view',
      });
    };

    initializeCalEmbed();
    return undefined;
  }, [attendeeEmail, attendeeName, calNamespace, inlineElementId, isLoading, prefilledCalLink]);

  return (
    <div className="schedule-page">
      <section className="schedule-intro" aria-labelledby="schedule-title">
        <p className="schedule-kicker">Appointments</p>
        <h1 id="schedule-title">Schedule a time with Hogcloud</h1>
        <p>
          Pick a time that works for you and tell us a little about what you
          want to build, print, fix, or explore.
        </p>
      </section>

      <section className="schedule-shell" aria-label="Appointment scheduler">
        <div
          key={inlineElementId}
          className="schedule-inline"
          id={inlineElementId}
          aria-label="Cal.com appointment calendar"
        />
      </section>
    </div>
  );
};

export default Schedule;
